import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Share } from '@material-ui/icons';
import tinycolor from 'tinycolor2';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import _ from 'underscore';

import { DateTimeHelper } from '../../util/date-time-helper';
import { getEmbedShareSettings, getPreviewLink, isTouchable, joinClassNames } from '../../util/util';

import ControlButton from '../Controls/ControlButton';

import './CountDown.scss';
import { useConfiguration } from '../../contexts/ConfigurationContext';

const DEFAULT_COUNTDOWN_CONFIG = {
	customBackground: {
		url: '',
	},
	titlePosition: {
		x: 10,
		y: 15,
		fontsize: 'large',
	},
	eventStartPosition: {
		x: 10,
		y: 10,
		fontsize: 'small',
	},
	countdownPosition: {
		x: 10,
		y: 45,
		fontsize: 'small',
	},
};
const DEFAULT_COUNTDOWN_CONFIG_MOBILE = {
	customBackground: {
		url: '',
	},
	titlePosition: {
		x: 10,
		y: 15,
		fontsize: 'large',
	},
	eventStartPosition: {
		x: 10,
		y: 10,
		fontsize: 'small',
	},
	countdownPosition: {
		x: 10,
		y: 45,
		fontsize: 'small',
	},
};

const COUNTDOWN_FONT_SIZE = {
	large: 1.167,
	medium: 1,
	small: 0.778,
};

const SCALE_FACTOR_WITH_BG_IMAGE = 0.034;

interface CountDownProps {
	playerConfigs?: any;
	isMobile?: boolean;
	isInlinePlayer?: boolean;
}

const baseClassName = 'countdown';

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

const CountDown = forwardRef(({ playerConfigs, isInlinePlayer, isMobile }: CountDownProps, ref) => {
	const { t, i18n } = useTranslation();
	const { mediaName, currentMediaId, mediasData, showRegistrationForm, showCountdownModule, mediaType, accountId, embedShareSettings: embedShareSettingsMessage, configParams, countdownStartTime, setOpenSharingModal, setShowCountdownModule } = useConfiguration();

	const countdownInterval = useRef<any | null>(null);
	const [isInvisible, setIsInvisible] = useState(true);
	const [isTitleClamped, setIsTitleClamped] = useState(false);
	const [limitedTitleLines, setLimitedTitleLines] = useState(3);
	const [countdownAriaLabel, setCountdownAriaLabel] = useState('');

	const containerRef = useRef<any>(null);
	const [fontSizeImageBase, setFontSizeImageBase] = useState(0);
	const [imageRect, setImageRect] = useState({ left: 0, top: 0, width: 0, height: 0 });

	const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });
	const color = useMemo(() => (!playerConfigs ? '#126cfc' : playerConfigs?.playerStyling?.primaryColor ?? '#126cfc'), [playerConfigs]);
	const opacity = Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isTouchable() ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const solidColorStyle = playerConfigs?.topControls?.showSolidBackgroundTopControl
		? {
			backgroundColor: tinycolor({
				...tinycolor(playerConfigs?.topControls?.solidBackgroundTopColor ?? '#000000').toRgb(),
				a: Number(playerConfigs?.topControls?.solidBackgroundTopControlOpacity ?? 100) / 100,
			}).toRgbString(),
		}
		: undefined;

	const showShareButton = useMemo(() => {
		if (configParams?.sharing) {
			return configParams?.sharing === "true";
		}
		return playerConfigs?.topControls?.enabled && playerConfigs?.topControls?.showSocialSharing;
	}, [playerConfigs?.topControls?.enabled, playerConfigs?.topControls?.showSocialSharing, configParams?.sharing]);

	const currentMediaData = useMemo(() => {
		return (mediasData ?? []).find((m: any) => m.id === currentMediaId) ?? {};
	}, [currentMediaId, mediasData]);

	const countdownConfig = useMemo(() => {
		const defaultConfig = isMobile && !isInlinePlayer ? DEFAULT_COUNTDOWN_CONFIG_MOBILE : DEFAULT_COUNTDOWN_CONFIG;
		const finalConfig = { ...defaultConfig, order: ['eventStartPosition', 'titlePosition', 'countdownPosition'] };
		const liveCountdown = playerConfigs?.liveCountdown ?? {};
		if (!liveCountdown || !liveCountdown.coverStyle || liveCountdown.coverStyle === 'default') {
			return finalConfig;
		}

		const customBackground = liveCountdown.customBackground ?? {};
		const titlePosition = liveCountdown.titlePosition ?? {};
		const countdownPosition = liveCountdown.countdownPosition ?? {};
		const eventStartPosition = liveCountdown.eventStartPosition ?? {};

		finalConfig.customBackground.url =
			customBackground.enabled && customBackground.url ? customBackground.url : defaultConfig.customBackground.url;
		finalConfig.titlePosition = titlePosition.enabled
			? {
				x: titlePosition.x ?? defaultConfig.titlePosition.x,
				y: titlePosition.y ?? defaultConfig.titlePosition.y,
				fontsize: titlePosition.fontsize ?? defaultConfig.titlePosition.fontsize,
			}
			: defaultConfig.titlePosition;
		finalConfig.eventStartPosition = eventStartPosition.enabled
			? {
				x: eventStartPosition.x ?? defaultConfig.eventStartPosition.x,
				y: eventStartPosition.y ?? defaultConfig.eventStartPosition.y,
				fontsize: eventStartPosition.fontsize ?? defaultConfig.eventStartPosition.fontsize,
			}
			: defaultConfig.eventStartPosition;
		finalConfig.countdownPosition = countdownPosition.enabled
			? {
				x: countdownPosition.x ?? defaultConfig.countdownPosition.x,
				y: countdownPosition.y ?? defaultConfig.countdownPosition.y,
				fontsize: countdownPosition.fontsize ?? defaultConfig.countdownPosition.fontsize,
			}
			: defaultConfig.countdownPosition;
		return finalConfig;
	}, [isInlinePlayer, playerConfigs?.liveCountdown]);

	const evtStartTimeRaw = useMemo(() => {
		return countdownStartTime ?? currentMediaData?.custom?.['event-start'];
	}, [currentMediaData?.custom, countdownStartTime]);
	const messageLabel = countdownStartTime ? t('VIDEO_WILL_START_IN') : t('LABEL_LIVE_START_IN');

	const onShareButtonClick = useCallback((_event: any) => {
		if (isMobile) {
			const params = {
				mediaDetails: {
					id: mediaType === 'scenario' ? configParams.scenarioId : configParams.mediaId,
					title: mediaName,
				},
				configs: { id: configParams.configId, ...playerConfigs },
				paramConfigs: configParams,
				accountId: accountId,
				type: mediaType,
				time: undefined,
				embedShareSettings: embedShareSettings,
			};
			navigator.share && navigator.share({ url: getPreviewLink(params) });
			return;
		}
		setOpenSharingModal(true);
	}, [isMobile, setOpenSharingModal, mediaType, configParams, mediaName, playerConfigs, accountId, embedShareSettings]);

	const checkToShowCountdown = useCallback(() => {
		const evtStartTime = Date.parse(evtStartTimeRaw);
		if (DateTimeHelper.checkTimeGreaterThan5Mins(evtStartTime) && DateTimeHelper.checkTimeDivideByNumber(evtStartTime)) {
			setCountdownAriaLabel(`${messageLabel} ${DateTimeHelper.formatHumanize(evtStartTimeRaw, t)}`);
		}
		if (!DateTimeHelper.checkTimeGreaterThan5Mins(evtStartTime) && DateTimeHelper.checkTimeDivideByNumber(evtStartTime, 1)) {
			setCountdownAriaLabel(`${messageLabel} ${DateTimeHelper.formatHumanize(evtStartTimeRaw, t)}`);
		}

		if (Date.now() < evtStartTime) {
			setShowCountdownModule(true);
		} else {
			setShowCountdownModule(false);
			countdownInterval.current && clearInterval(countdownInterval.current);
		}
	}, [currentMediaData?.custom, t]);

	const _handleReflow = useCallback((rleState) => {
		const { clamped } = rleState;
		setIsTitleClamped(clamped);
		return rleState;
	}, []);

	useEffect(() => {
		checkToShowCountdown();
		countdownInterval.current = setInterval(function () {
			checkToShowCountdown();
		}, 1000);

		return () => countdownInterval.current && clearInterval(countdownInterval.current);
	}, [checkToShowCountdown, currentMediaData.custom, showCountdownModule]);

	useEffect(() => {
		const timeoutRef = setTimeout(() => setIsInvisible(false), 1000);
		return () => clearTimeout(timeoutRef);
	}, []);

	useEffect(() => {
		isInvisible &&
			setCountdownAriaLabel(`${messageLabel} ${DateTimeHelper.formatHumanize(evtStartTimeRaw, t)}`);
	}, [currentMediaData.custom, evtStartTimeRaw, isInvisible, messageLabel, t]);

	useLayoutEffect(() => {
		if (!showCountdownModule || !containerRef.current) {
			return;
		}

		const updateImageRect = (width: number, height: number) => {
			const container = containerRef.current;
			if (!container) {
				return;
			}
			const containerWidth = container.offsetWidth;
			const containerHeight = container.offsetHeight;
			const imgRatio = width / height;
			const containerRatio = containerWidth / containerHeight;

			let renderedWidth, renderedHeight, left, top;
			if (imgRatio > containerRatio) {
				// Image fills container width, centered vertically
				renderedWidth = containerWidth;
				renderedHeight = containerWidth / imgRatio;
				left = 0;
				top = (containerHeight - renderedHeight) / 2;
			} else {
				// Image fills container height, centered horizontally
				renderedHeight = containerHeight;
				renderedWidth = containerHeight * imgRatio;
				left = (containerWidth - renderedWidth) / 2;
				top = 0;
			}
			setImageRect({ left, top, width: renderedWidth, height: renderedHeight });
			setFontSizeImageBase(renderedWidth * SCALE_FACTOR_WITH_BG_IMAGE);
		};

		let resizeObserver: any = null;
		if (containerRef.current) {
			if (countdownConfig?.customBackground?.url) {
				const img = document.createElement('img');
				img.src = countdownConfig?.customBackground?.url;

				img.onload = () => {
					if (!containerRef.current) { return; }
					const width = img.width;
					const height = img.height;
					updateImageRect(width, height);
					resizeObserver = new window.ResizeObserver(() => updateImageRect(width, height));
					resizeObserver.observe(containerRef.current);
				};
			} else {
				const width = containerRef.current.offsetWidth;
				const height = containerRef.current.offsetHeight;
				updateImageRect(width, height);
				resizeObserver = new window.ResizeObserver(() => updateImageRect(width, height));
				resizeObserver.observe(containerRef.current);
			}
		 }

		return () => {
			resizeObserver && resizeObserver.disconnect();
		};
	}, [countdownConfig?.customBackground?.url, showCountdownModule]);

	useImperativeHandle(ref, () => ({
		changeLimitedTitleLines: (value: number) => {
			if (value !== limitedTitleLines) {
				setLimitedTitleLines(value);
			}
		},
	}));

	const renderTitle = () => {
		if (!currentMediaData?.mediaName) {
			return null;
		}

		const qplayerWidth = configParams.container?.getBoundingClientRect()?.width || window.innerWidth;
		const hasBgImage = !!countdownConfig.customBackground.url;
		const topPos = hasBgImage
			? `${imageRect.top + (imageRect.height * countdownConfig.titlePosition.y) / 100}px`
			: `${countdownConfig.titlePosition.y}%`;
		const leftPos = hasBgImage
			? `${imageRect.left + (imageRect.width * countdownConfig.titlePosition.x) / 100}px`
			: `${countdownConfig.titlePosition.x}%`;
		const selectedSize = COUNTDOWN_FONT_SIZE[(countdownConfig.titlePosition.fontsize as keyof typeof COUNTDOWN_FONT_SIZE) || 'medium'];
		const calculatedFontSize = hasBgImage ? fontSizeImageBase * selectedSize : qplayerWidth * SCALE_FACTOR_WITH_BG_IMAGE * selectedSize;

		return (
			<div
				key="titlePosition"
				className={joinClassNames(`${baseClassName}__title qplayer-light-font`)}
				aria-label={`${t('ARIA_LABEL_MEDIA_TITLE')}${currentMediaData.mediaName}`}
				style={{
					top: topPos,
					left: leftPos,
					right: leftPos,
					fontSize: `${calculatedFontSize}px`,
					...(configParams.isBonnierDi ? { fontFamily: "'BentonSansCondDI Black', sans-serif" } : {}),
				}}
				role="heading"
				aria-level={1}
			>
				<ResponsiveEllipsis
					aria-hidden={true}
					className="qplayer-light-font"
					text={currentMediaData.mediaName}
					maxLine={limitedTitleLines}
					ellipsis="..."
					trimRight
					basedOn="words"
					onReflow={_handleReflow}
				/>

				{isTitleClamped && (
					<div style={{ position: 'relative' }} aria-hidden={true}>
						<div
							aria-hidden={true}
							className={joinClassNames([
								'controlbar__control-tooltip',
								'controlbar__control-tooltip--light',
								`${baseClassName}__title--tooltip`,
								'countdown__font-small',
							])}
						>
							<div className="controlbar__control-tooltip-text qplayer-light-font countdown__font-small">
								{currentMediaData.mediaName}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderEventStart = () => {
		const qplayerWidth = configParams.container?.getBoundingClientRect()?.width || window.innerWidth;
		const hasBgImage = !!countdownConfig.customBackground.url;
		const topPos = hasBgImage
			? `${imageRect.top + (imageRect.height * countdownConfig.eventStartPosition.y) / 100}px`
			: `${countdownConfig.eventStartPosition.y}%`;
		const leftPos = hasBgImage
			? `${imageRect.left + (imageRect.width * countdownConfig.eventStartPosition.x) / 100}px`
			: `${countdownConfig.eventStartPosition.x}%`;
		const selectedSize = COUNTDOWN_FONT_SIZE[(countdownConfig.eventStartPosition.fontsize as keyof typeof COUNTDOWN_FONT_SIZE) || 'medium'];
		const calculatedFontSize = hasBgImage ? fontSizeImageBase * selectedSize : qplayerWidth * SCALE_FACTOR_WITH_BG_IMAGE * selectedSize;

		return (
			<div
				key={'eventStartPosition'}
				className={joinClassNames(`${baseClassName}__event-start-time`, 'qplayer-semibold-font')}
				style={{
					top: topPos,
					left: leftPos,
					fontSize: `${calculatedFontSize}px`,
				}}
				aria-label={`${t('ARIA_LABEL_START_DATE')}, ${DateTimeHelper.formatDate(evtStartTimeRaw, t, i18n.language)}`}
			>
				<span className={'qplayer-semibold-font'} aria-hidden={true}>
					{DateTimeHelper.formatDate(evtStartTimeRaw, t, i18n.language)}
				</span>
			</div>
		);
	};

	const renderCountdown = () => {
		const qplayerWidth = configParams.container?.getBoundingClientRect()?.width || window.innerWidth;
		const evtStartTime = typeof showCountdownModule !== 'boolean' ? Date.parse(showCountdownModule) : Date.parse(evtStartTimeRaw);
		const countDownTime = Date.now() < evtStartTime ? DateTimeHelper.parseTimeToNumber(evtStartTimeRaw) : '00:00';

		const hasBgImage = !!countdownConfig.customBackground.url;
		const topPos = hasBgImage
			? `${imageRect.top + (imageRect.height * countdownConfig.countdownPosition.y) / 100}px`
			: `${(containerRef?.current?.offsetHeight * countdownConfig.countdownPosition.y) / 100}px`;

		const leftPos = hasBgImage
			? `${imageRect.left + (imageRect.width * countdownConfig.countdownPosition.x) / 100}px`
			: `${countdownConfig.countdownPosition.x}%`;
		const selectedSize = COUNTDOWN_FONT_SIZE[(countdownConfig.countdownPosition.fontsize as keyof typeof COUNTDOWN_FONT_SIZE) || 'medium'];
		const calculatedFontSize = hasBgImage ? fontSizeImageBase * selectedSize : qplayerWidth * SCALE_FACTOR_WITH_BG_IMAGE * selectedSize;

		return (
			<div
				key={'countdownPosition'}
				className={joinClassNames(`${baseClassName}__event-countdown`, 'qplayer-semibold-font')}
				style={{
					top: topPos,
					left: leftPos,
					fontSize: `${calculatedFontSize}px`,
				}}
			>
				<div className="qplayer-semibold-font" aria-label={countdownAriaLabel} role="alert" aria-live="assertive">
					<span className={'qplayer-semibold-font'} aria-hidden={true}>
						{messageLabel}
					</span>{' '}
					<div className={'qplayer-semibold-font'} aria-hidden={true}>
						{countDownTime}
					</div>
				</div>
			</div>
		);
	};

	// Make sure tab order is the same as what shows screen
	const renderOrder = _.sortBy(
		[
			{ element: 'eventStartPosition', value: countdownConfig.eventStartPosition.y },
			{ element: 'titlePosition', value: countdownConfig.titlePosition.y },
			{ element: 'countdownPosition', value: countdownConfig.countdownPosition.y },
		],
		'value'
	).map((m) => m.element);

	if ((!showCountdownModule && !countdownStartTime)|| showRegistrationForm) {
		return null;
	}

	return (
		<div
			ref={containerRef}
			className={joinClassNames(
				baseClassName,
				isMobile ? `${baseClassName}-mobile` : undefined,
				isInvisible ? `${baseClassName}--invisible` : undefined
			)}
			style={{
				backgroundImage: `url("${countdownConfig.customBackground.url}")`,
				...{ backgroundColor: tinycolor(rgbaWithOpa).setAlpha(0.5).toRgbString() },
			}}
		>
			{renderOrder.map((element) => {
				if (element === 'eventStartPosition') {
					return renderEventStart();
				}
				if (element === 'countdownPosition') {
					return renderCountdown();
				}
				if (element === 'titlePosition') {
					return renderTitle();
				}
				return null;
			})}

			{showShareButton && (
				<ControlButton
					icon={<Share htmlColor="currentColor" titleAccess={t('SHARE_TOOLTIP')} aria-hidden={true} />}
					tabIndex={0}
					ariaLabel={t('SHARE')}
					tooltip={t('SHARE_TOOLTIP')}
					style={solidColorStyle}
					hoverStyle={hoverStyle}
					onClick={onShareButtonClick}
					className={`${baseClassName}__share-btn qplayer_share_btn`}
					topControl
				/>
			)}
		</div>
	);
});

export default CountDown;
